/**
 * Sticky Search Bar — assets/sticky-search-bar.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom element <sticky-search-bar>.
 *
 * Visibility
 *  The section is hidden (display:none) by default. It opens when the header's
 *  .show-search-link is clicked, and closes on ×, Escape, or outside click.
 *  A capture-phase listener on <page-header> intercepts the click BEFORE
 *  main.js's delegate handler can add 'show-search' to <body>, so the old
 *  full-screen overlay never appears.
 *
 * Default panel (empty state)
 *  When the bar opens and the input is empty, a three-panel overlay shows:
 *    A. Recent Searches  — pill tags from localStorage, hidden on first visit
 *    B. Popular Searches — curated terms from Theme Editor blocks
 *    C. Best Sellers     — product cards from Theme Editor product picker blocks
 *  JS adds .ssb--show-panel to reveal this. Clicking any term or tag populates
 *  the input and triggers a predictive search, and saves to recent searches.
 *
 * Predictive search
 *  On input (debounced 400 ms) fetches Shopify Predictive Search JSON API.
 *  Renders products (image + title + price), collections, and pages in a
 *  dropdown. A "View all" footer link submits the full search.
 *  JS adds .ssb--show-results to reveal the predictive dropdown.
 *  The panel and results are mutually exclusive.
 *
 * Keyboard
 *  ArrowDown/Up to navigate results, Enter to follow, Escape to close bar.
 *
 * Recent searches storage key: 'ssb_recent_searches' (localStorage, max 5).
 *
 * No third-party dependencies — plain ES6.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  if (customElements.get('sticky-search-bar')) return;

  const StickySearchBar = class extends HTMLElement {

    /* ── Lifecycle ──────────────────────────────────────────────────────── */

    connectedCallback() {
      // DOM refs
      this.input        = this.querySelector('.ssb__input');
      this.clearBtn     = this.querySelector('.ssb__clear');
      this.form         = this.querySelector('.ssb__form');
      this.resultsEl    = this.querySelector('.ssb__results');
      this.panelEl      = this.querySelector('.ssb__panel');
      this.recentEl     = this.querySelector('.ssb__panel-recent');
      this.recentTagsEl    = this.querySelector('.ssb__recent-tags');
      this.placeholderSpan = this.querySelector('.ssb__placeholder-anim');
      this.sectionEl       = this.closest('[id^="shopify-section-"]');

      // Config from Liquid data attributes
      this.isQuickSearch = this.dataset.quickSearch !== 'false';
      this.maxResults    = parseInt(this.dataset.maxResults, 10) || 6;
      this.moneyFormat   = this.dataset.moneyFormat || 'R{{amount}}';
      this._isInline     = this.dataset.openOnLoad === 'true';

      const rawTypes    = this.dataset.searchTypes || 'product,collection';
      this.resourceTypes = rawTypes === 'all' ? 'product,collection,page' : rawTypes;

      // State
      this._oldValue      = '';
      this._debounceTimer = null;
      this._abort         = null;
      this._activeIndex         = -1;
      this._placeholderTerms    = [];
      this._placeholderIndex    = 0;
      this._placeholderInterval = null;
      try {
        const raw = this.dataset.placeholderTerms;
        if (raw) this._placeholderTerms = JSON.parse(raw).filter(Boolean);
      } catch { /* malformed JSON — ignore */ }

      this._adjustStickyTop();
      this._interceptHeaderSearch();
      this._bindEvents();

      // Inline mode — apply CSS class; bar is always visible, no open/close needed
      if (this._isInline) {
        this.classList.add('ssb--inline');
        if (this.sectionEl) this.sectionEl.classList.add('ssb--inline');
      }

      // Animated placeholder — clear native placeholder and start cycling
      if (this._placeholderTerms.length > 0 && this.placeholderSpan) {
        this.input.setAttribute('placeholder', '');
        this._startPlaceholderCycle();
      }
    }

    disconnectedCallback() {
      // Clean up the capture listener so we don't leak it if the section
      // is removed/re-added in the Theme Editor.
      const pageHeader = document.querySelector('page-header');
      if (pageHeader && this._headerClickHandler) {
        pageHeader.removeEventListener('click', this._headerClickHandler, true);
      }

      // Clear animated placeholder interval
      if (this._placeholderInterval) {
        clearInterval(this._placeholderInterval);
        this._placeholderInterval = null;
      }
    }

    /* ── Sticky top offset ──────────────────────────────────────────────── */

    /**
     * Sets the section's `top` to the current header height so the bar
     * sits flush below the sticky header rather than overlapping it.
     * Called once on mount and again each time the bar opens.
     */
    _adjustStickyTop() {
      // Overlay starts at top:0 and covers the header — no dynamic offset needed.
    }

    /* ── Intercept the header search icon click ─────────────────────────── */

    /**
     * Adds a CAPTURE listener to <page-header>.
     *
     * Capture fires before bubble, so we intercept the click before main.js's
     * delegated bubble listener adds 'show-search' to <body>.
     * stopImmediatePropagation() prevents the event from propagating further,
     * so the old overlay never opens.
     */
    _interceptHeaderSearch() {
      if (this._isInline) return; // inline mode — leave header icon behaviour unchanged
      const pageHeader = document.querySelector('page-header');
      if (!pageHeader) return; // no header on this page — bar stays hidden

      this._headerClickHandler = (evt) => {
        if (!evt.target.closest('.show-search-link')) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this._toggleBar();
      };

      pageHeader.addEventListener('click', this._headerClickHandler, true);
    }

    /* ── Bar open / close ───────────────────────────────────────────────── */

    _toggleBar() {
      if (!this.sectionEl) return;
      this.sectionEl.classList.contains('ssb-bar--open')
        ? this._closeBar()
        : this._openBar();
    }

    _openBar() {
      if (!this.sectionEl) return;
      this._adjustStickyTop();                          // refresh in case header resized
      this.sectionEl.classList.remove('ssb-bar--closing');
      this.sectionEl.classList.add('ssb-bar--open');
      // Show the default panel if the input is empty
      this._renderPanel();
      // Slight delay so the animation starts before focus steals layout
      requestAnimationFrame(() => this.input.focus());
    }

    _closeBar() {
      if (this._isInline) return; // inline mode — bar is always visible
      if (!this.sectionEl) return;
      if (!this.sectionEl.classList.contains('ssb-bar--open')) return; // already closed

      this._closeResults();
      this.sectionEl.classList.add('ssb-bar--closing');

      this.sectionEl.addEventListener('animationend', () => {
        this.sectionEl.classList.remove('ssb-bar--open', 'ssb-bar--closing');
        // Reset state so next open starts clean
        this.input.value = '';
        this._oldValue   = '';
        this.classList.remove('ssb--has-value', 'ssb--show-panel');
      }, { once: true });
    }

    /* ── Event bindings ─────────────────────────────────────────────────── */

    _bindEvents() {
      // Live search on keystroke
      if (this.isQuickSearch) {
        this.input.addEventListener('input', this._onInput.bind(this));
      }

      // Keyboard navigation within the dropdown
      this.input.addEventListener('keydown', this._onKeydown.bind(this));

      // × button closes the entire bar
      this.clearBtn.addEventListener('click', () => this._closeBar());

      // Show/hide the × button based on whether the field has content
      this.input.addEventListener('input', () => {
        this.classList.toggle('ssb--has-value', this.input.value.length > 0);
      });

      // Re-show cached results (or panel) when the input regains focus mid-session
      this.input.addEventListener('focus', () => {
        // Hide animated placeholder while focused — cursor is visible
        if (this.placeholderSpan) this.placeholderSpan.style.display = 'none';

        if (this.input.value.length > 0 && this.resultsEl.children.length > 0) {
          this.classList.add('ssb--show-results');
          this.input.setAttribute('aria-expanded', 'true');
        } else if (this.input.value.length === 0) {
          this._renderPanel();
        }
      });

      // Restore animated placeholder on blur if input is empty
      this.input.addEventListener('blur', () => {
        if (this.placeholderSpan && this.input.value.length === 0 && this._placeholderTerms.length > 0) {
          this.placeholderSpan.style.display = '';
        }
      });

      // Save recent search on form submit (user typed and pressed Enter / submit button)
      if (this.form) {
        this.form.addEventListener('submit', () => {
          this._saveRecentSearch(this.input.value.trim());
        });
      }

      // Popular search term clicks — event delegation on the panel
      if (this.panelEl) {
        this.panelEl.addEventListener('click', (e) => {
          const btn = e.target.closest('.ssb__popular-term');
          if (btn) this._triggerSearch(btn.dataset.term);
        });
      }

      // Overlay mode: close the bar on outside click.
      // Inline mode: close only the dropdown/panel — bar stays visible.
      document.addEventListener('click', (e) => {
        if (!this.sectionEl || this.sectionEl.contains(e.target)) return;
        if (this._isInline) {
          this._closeResults();
        } else if (this.sectionEl.classList.contains('ssb-bar--open')) {
          this._closeBar();
        }
      }, { passive: true });

      // Re-calculate sticky offset on resize
      const onResize = () => this._adjustStickyTop();
      window.addEventListener('resize', onResize, { passive: true });
      window.addEventListener('on:debounced-resize', onResize);
    }

    /* ── Input handler ──────────────────────────────────────────────────── */

    _onInput() {
      const value = this.input.value.trim();

      this.classList.toggle('ssb--has-value', value.length > 0);

      if (!value) {
        this._oldValue = '';
        if (this._abort) { this._abort.abort(); this._abort = null; }
        clearTimeout(this._debounceTimer);
        this._closeResults();
        this._renderPanel();  // restore default panel when input is cleared
        return;
      }

      // Hide the default panel as soon as the user starts typing
      this.classList.remove('ssb--show-panel');

      if (value === this._oldValue) return;
      this._oldValue = value;

      clearTimeout(this._debounceTimer);
      this._showSpinner();
      this._debounceTimer = setTimeout(() => this._fetchResults(value), 400);
    }

    /* ── Keyboard navigation ────────────────────────────────────────────── */

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this._closeBar();
        return;
      }

      const items = [...this.resultsEl.querySelectorAll('.ssb__result-item')];
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this._activeIndex = Math.min(this._activeIndex + 1, items.length - 1);
        this._highlightItem(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this._activeIndex = Math.max(this._activeIndex - 1, 0);
        this._highlightItem(items);
      } else if (e.key === 'Enter' && this._activeIndex >= 0) {
        e.preventDefault();
        const link = items[this._activeIndex].querySelector('a');
        if (link) link.click();
      }
    }

    _highlightItem(items) {
      items.forEach((el) => el.classList.remove('ssb--active'));
      const active = items[this._activeIndex];
      if (active) {
        active.classList.add('ssb--active');
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    /* ── Fetch predictive search results ────────────────────────────────── */

    _fetchResults(query) {
      if (this._abort) this._abort.abort();
      this._abort = new AbortController();

      const url = new URL(window.location.origin + theme.routes.predictiveSearch);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', this.resourceTypes);
      url.searchParams.set('resources[limit]', this.maxResults);
      url.searchParams.set('resources[options][fields]',
        'title,product_type,variants.title,vendor');

      fetch(url.toString(), {
        signal: this._abort.signal,
        headers: { 'Accept': 'application/json' }
      })
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then((data) => {
          this._abort = null;
          this._renderResults(data, query);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          console.warn('[StickySearchBar]', err);
          this._showEmpty(query);
        });
    }

    /* ── Render results dropdown ────────────────────────────────────────── */

    _renderResults(data, query) {
      const res = data && data.resources && data.resources.results;
      if (!res) { this._showEmpty(query); return; }

      const products    = res.products    || [];
      const collections = res.collections || [];
      const pages       = res.pages       || [];

      const combined = [
        ...products.map((p)  => ({ kind: 'product',    item: p  })),
        ...collections.map((c) => ({ kind: 'collection', item: c  })),
        ...pages.map((pg)  => ({ kind: 'page',       item: pg }))
      ].slice(0, this.maxResults);

      if (!combined.length) { this._showEmpty(query); return; }

      this._activeIndex = -1;

      const list = document.createElement('ul');
      list.className = 'ssb__results-list';

      combined.forEach(({ kind, item }, i) => {
        const li = document.createElement('li');
        li.className = 'ssb__result-item';
        li.id        = 'ssb-result-' + this.dataset.sectionId + '-' + i;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');

        // Image
        const fi = item.featured_image;
        let imgHtml;
        if (fi && fi.url) {
          const src = fi.url + (fi.url.includes('?') ? '&' : '?') + 'width=88';
          imgHtml = '<img class="ssb__result-img" src="' + src
            + '" alt="' + this._esc((fi.alt || item.title)) + '"'
            + ' width="44" height="44" loading="lazy">';
        } else {
          imgHtml = '<div class="ssb__result-img" aria-hidden="true"></div>';
        }

        // Meta + price
        let metaText = '', priceHtml = '';
        if (kind === 'product') {
          metaText  = item.vendor || '';
          priceHtml = item.price_min
            ? '<span class="ssb__result-price">'
                + this._formatMoney(item.price_min, this.moneyFormat)
                + '</span>'
            : '';
        } else if (kind === 'collection') {
          metaText = 'Collection';
        } else {
          metaText = 'Page';
        }

        li.innerHTML =
          '<a class="ssb__result-link" href="' + item.url + '">'
          + imgHtml
          + '<span class="ssb__result-info">'
          +   '<span class="ssb__result-title">' + this._esc(item.title) + '</span>'
          +   (metaText ? '<span class="ssb__result-meta">' + this._esc(metaText) + '</span>' : '')
          + '</span>'
          + priceHtml
          + '</a>';

        list.appendChild(li);
      });

      // "View all" footer
      const typeParam  = this.form && this.form.querySelector('[name="type"]')
        ? '&type=' + this.form.querySelector('[name="type"]').value
        : '';
      const formAction = (this.form && this.form.getAttribute('action')) || '/search';
      const allHref    = formAction + '?q=' + encodeURIComponent(query) + typeParam;

      const footer = document.createElement('div');
      footer.className = 'ssb__footer';
      footer.innerHTML =
        '<a class="ssb__footer-link" href="' + allHref + '">'
        + 'View all results for \u201c' + this._esc(query) + '\u201d'
        + '</a>';

      this.resultsEl.innerHTML = '';
      this.resultsEl.appendChild(list);
      this.resultsEl.appendChild(footer);

      this.classList.remove('ssb--show-panel');
      this.classList.add('ssb--show-results');
      this.input.setAttribute('aria-expanded', 'true');
    }

    /* ── Loading / empty states ─────────────────────────────────────────── */

    _showSpinner() {
      this.classList.remove('ssb--show-panel');
      this.resultsEl.innerHTML =
        '<div class="ssb__spinner" aria-hidden="true">'
        + '<div class="ssb__spinner-dot"></div>'
        + '<div class="ssb__spinner-dot"></div>'
        + '<div class="ssb__spinner-dot"></div>'
        + '</div>';
      this.classList.add('ssb--show-results');
    }

    _showEmpty(query) {
      this.classList.remove('ssb--show-panel');
      this.resultsEl.innerHTML =
        '<p class="ssb__empty">No results found'
        + (query ? ' for \u201c' + this._esc(query) + '\u201d' : '')
        + '</p>';
      this.classList.add('ssb--show-results');
      this.input.setAttribute('aria-expanded', 'false');
    }

    /* ── Close results dropdown only (bar stays open) ───────────────────── */

    _closeResults() {
      this.classList.remove('ssb--show-results', 'ssb--show-panel');
      this.input.setAttribute('aria-expanded', 'false');
      this._activeIndex = -1;
      if (this._abort) { this._abort.abort(); this._abort = null; }
      clearTimeout(this._debounceTimer);
    }

    /* ── Default panel — recent / popular / best sellers ────────────────── */

    /**
     * Reads recent searches from localStorage and renders the tag row.
     * Then checks if any panel content exists (recent tags, popular searches,
     * or bestseller products) and only shows the panel if something is present.
     * Safe to call any time — no-ops if input has a value.
     */
    _renderPanel() {
      if (!this.panelEl) return;
      if (this.classList.contains('ssb--has-value')) return;

      // Panel A: recent searches
      const recent = this._getRecentSearches();
      if (this.recentEl && this.recentTagsEl) {
        if (recent.length > 0) {
          this.recentTagsEl.innerHTML = '';
          recent.forEach((term) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'ssb__recent-tag';
            btn.setAttribute('role', 'listitem');
            btn.textContent = term;
            btn.addEventListener('click', () => this._triggerSearch(term));
            this.recentTagsEl.appendChild(btn);
          });
          this.recentEl.style.display = '';
        } else {
          // Hide the recent row entirely on first visit (no history yet)
          this.recentEl.style.display = 'none';
        }
      }

      // Only show the panel if at least one section has content
      const hasRecent      = recent.length > 0;
      const hasPopular     = !!this.querySelector('.ssb__panel-popular');
      const hasBestsellers = !!this.querySelector('.ssb__panel-bestsellers');

      if (hasRecent || hasPopular || hasBestsellers) {
        this.classList.add('ssb--show-panel');
      }
    }

    /**
     * Populates the input with a term, hides the panel, saves to recent
     * searches, and triggers a live predictive search fetch.
     * Used by both recent-tag clicks and popular-term clicks.
     */
    _triggerSearch(term) {
      if (!term) return;
      this._saveRecentSearch(term);
      this.input.value = term;
      this.classList.add('ssb--has-value');
      this.classList.remove('ssb--show-panel');
      this._oldValue = ''; // force a fresh fetch even if this term was searched before
      this._onInput();
    }

    /* ── Recent searches (localStorage) ────────────────────────────────── */

    _getRecentSearches() {
      try {
        const stored = localStorage.getItem('ssb_recent_searches');
        const parsed = JSON.parse(stored || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    /**
     * Prepends term to the saved list, deduplicates, caps at 5 entries.
     * Silently no-ops if localStorage is unavailable.
     */
    _saveRecentSearch(term) {
      if (!term || !term.trim()) return;
      const clean = term.trim();
      try {
        let searches = this._getRecentSearches();
        searches = [clean, ...searches.filter((s) => s !== clean)].slice(0, 5);
        localStorage.setItem('ssb_recent_searches', JSON.stringify(searches));
      } catch {
        // localStorage unavailable — silently ignore
      }
    }

    /* ── Animated placeholder cycling ──────────────────────────────────── */

    _startPlaceholderCycle() {
      const terms    = this._placeholderTerms;
      const prefix   = this.dataset.placeholderPrefix || '';
      const interval = (parseInt(this.dataset.placeholderInterval, 10) || 3) * 1000;
      const span     = this.placeholderSpan;

      // Show first term immediately
      span.textContent = prefix.trimEnd() + ' ' + terms[this._placeholderIndex];

      this._placeholderInterval = setInterval(() => {
        // Fade out
        span.classList.add('ssb__placeholder-anim--hidden');
        setTimeout(() => {
          // Advance to next term
          this._placeholderIndex = (this._placeholderIndex + 1) % terms.length;
          span.textContent = prefix.trimEnd() + ' ' + terms[this._placeholderIndex];
          // Fade in
          span.classList.remove('ssb__placeholder-anim--hidden');
        }, 400);
      }, interval);
    }

    /* ── Helpers ────────────────────────────────────────────────────────── */

    /**
     * Format a price using the store's money format string.
     * Shopify Predictive Search returns price_min as a decimal string ("99.00").
     */
    _formatMoney(price, format) {
      if (!price || !format) return '';
      const amount          = parseFloat(price).toFixed(2);
      const amountNoDecimal = String(Math.floor(parseFloat(price)));
      return format
        .replace('{{amount}}',                                  amount)
        .replace('{{amount_no_decimals}}',                      amountNoDecimal)
        .replace('{{amount_with_comma_separator}}',             amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimal);
    }

    /** Minimal HTML escaping to prevent XSS in dynamically-built strings. */
    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  };

  customElements.define('sticky-search-bar', StickySearchBar);

})();
